import * as React from "react";
import { motion } from "framer-motion";
import type { PresentationData } from "@/data/types";
import { fullAddressLine, locationLine } from "@/lib/format";
import { isPdfRenderMode } from "@/lib/pdfMode";

interface Props {
  data: PresentationData;
}

const EASE = [0.22, 1, 0.36, 1] as const;

export const PersonalHero: React.FC<Props> = ({ data }) => {
  const pdfMode = isPdfRenderMode();
  const { owner, property, agent, hero } = data;
  const cover = property.images?.[0];
  const location = locationLine(property);

  return (
    <section
      className="relative flex min-h-[92vh] w-full items-end overflow-hidden bg-charcoal"
      data-testid="personal-hero"
      aria-label="Personal opening"
    >
      {cover && (
        <div className="absolute inset-0">
          <img
            src={cover.url}
            alt={cover.alt || `${property.address}`}
            className="hero-kenburns h-full w-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/55 to-charcoal/20" />
          <div className="absolute inset-0 grain-overlay opacity-[0.12]" />
        </div>
      )}

      <div className="relative z-10 mx-auto w-full max-w-5xl px-6 pb-16 pt-28 md:px-10 md:pb-24">
        <motion.div
          initial={pdfMode ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: pdfMode ? 0 : 0.8, ease: EASE, delay: pdfMode ? 0 : 0.15 }}
          className="max-w-2xl"
        >
          <div className="mb-6 flex items-center gap-3 text-ivory/70">
            <span className="h-px w-8 bg-brass" />
            <span className="text-[0.7rem] uppercase tracking-[0.28em]">
              {owner.firstName
                ? `Prepared for ${owner.firstName}${agent.brokerage ? ` · ${agent.brokerage}` : ""}`
                : agent.brokerage || "Private listing review"}
            </span>
          </div>

          <h1 className="font-serif text-4xl font-medium leading-[1.05] text-ivory sm:text-5xl lg:text-6xl">
            {hero.greetingLine}
          </h1>

          {hero.body && (
            <motion.p
              initial={pdfMode ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: pdfMode ? 0 : 0.8, ease: EASE, delay: pdfMode ? 0 : 0.4 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-ivory/85 md:text-lg"
            >
              {hero.body}
            </motion.p>
          )}

          {hero.followUp && (
            <motion.p
              initial={pdfMode ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: pdfMode ? 0 : 0.8, ease: EASE, delay: pdfMode ? 0 : 0.6 }}
              className="mt-4 font-serif text-xl italic text-ivory/95 md:text-2xl"
            >
              {hero.followUp}
            </motion.p>
          )}

          <div className="mt-8 flex items-baseline gap-3 text-ivory/70">
            <span className="font-serif text-lg text-ivory">
              {fullAddressLine(property)}
            </span>
            {location && (
              <span className="text-xs uppercase tracking-[0.2em]">{location}</span>
            )}
          </div>

          {hero.scrollCue && !pdfMode && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
              className="mt-12 flex items-center gap-3 text-ivory/70"
              data-pdf-hide
            >
              <span className="text-xs uppercase tracking-[0.24em]">
                {hero.scrollCue}
              </span>
              <motion.span
                aria-hidden
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                className="text-brass"
              >
                ↓
              </motion.span>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
